import { ContextMenu, IconButton, Menu, SNAPS, type MenuItem, type WindowSnap } from '@tomo/ui';
import { Columns2, Layers, Maximize2, Minus, MoreHorizontal, PanelLeft, PanelRight, X } from 'lucide-react';
import { useCollection, useRecord } from '../../data/hooks.tsx';
import { Icon } from '../../lib/icons.tsx';
import { useWindowManager, useWindows } from './hooks.ts';
import { isCompactDesk, type RecordWindow } from './windows.ts';
import './windows.css';

/** One button per open window, plus ways to arrange them. Shown while any window is open. */
export function Taskbar() {
  const manager = useWindowManager();
  const { wins, active, desk } = useWindows();
  if (!wins.length) return null;
  const visible = wins.filter((w) => !w.minimized).length;
  // On phones every window fills the desk: there is nothing to arrange.
  const compact = isCompactDesk(desk);
  const items: MenuItem[] = compact
    ? []
    : [
        { label: 'Side by side', icon: <Columns2 />, disabled: visible < 2, onSelect: () => manager.tile() },
        { label: 'Cascade', icon: <Layers />, disabled: !visible, onSelect: () => manager.cascade() },
      ];
  items.push(
    { label: 'Minimize all', icon: <Minus />, disabled: !visible, onSelect: () => manager.minimizeAll() },
    { type: 'separator' },
    { label: 'Close all', icon: <X />, danger: true, onSelect: () => manager.closeAll() },
  );
  return (
    <nav className="taskbar" aria-label="Open windows">
      <div className="taskbar__list">
        {wins.map((win) => (
          <TaskButton key={win.id} win={win} active={win.id === active} compact={compact} />
        ))}
      </div>
      {!compact && (
        <IconButton label="Side by side" size="sm" disabled={visible < 2} onClick={() => manager.tile()}>
          <Columns2 />
        </IconButton>
      )}
      <Menu
        placement="top-end"
        trigger={
          <IconButton label="Window options" size="sm">
            <MoreHorizontal />
          </IconButton>
        }
        items={items}
      />
    </nav>
  );
}

function TaskButton({ win, active, compact }: { win: RecordWindow; active: boolean; compact: boolean }) {
  const manager = useWindowManager();
  const record = useRecord(win.recordId);
  const collection = useCollection(record?.collectionId);
  const title = record?.title || 'Untitled';
  const snap = (slot: WindowSnap) => () => {
    manager.place(win.id, { snap: slot });
    manager.activate(win.id, true);
  };
  const items: MenuItem[] = [
    win.minimized ? { label: 'Restore', icon: <Maximize2 />, onSelect: () => manager.activate(win.id, true) } : { label: 'Minimize', icon: <Minus />, onSelect: () => manager.minimize(win.id) },
    ...(compact
      ? []
      : ([
          { label: 'Maximize', icon: <Maximize2 />, onSelect: snap(SNAPS.max) },
          { label: 'Snap left', icon: <PanelLeft />, onSelect: snap(SNAPS.left) },
          { label: 'Snap right', icon: <PanelRight />, onSelect: snap(SNAPS.right) },
        ] as MenuItem[])),
    { type: 'separator' },
    { label: 'Close', icon: <X />, onSelect: () => manager.close(win.id) },
  ];
  return (
    <ContextMenu items={items}>
      <button
        type="button"
        className="taskbar__item"
        data-active={active || undefined}
        data-minimized={win.minimized || undefined}
        aria-pressed={active && !win.minimized}
        title={collection ? `${title} · ${collection.name}` : title}
        onClick={() => manager.toggle(win.id)}
        // Middle-click closes, like a browser tab.
        onMouseDown={(e) => e.button === 1 && e.preventDefault()}
        onAuxClick={(e) => e.button === 1 && manager.close(win.id)}
      >
        <Icon token={record?.icon ?? collection?.icon} size={14} />
        <span className="g-truncate">{title}</span>
      </button>
    </ContextMenu>
  );
}
